import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import FormControl from './FormControl';
import DateTimePicker from 'react-datetime-picker';
import moment from 'moment';
import { GetValidationPattern } from '../../../util/ValidationUtil.js';
import i18n from '../../../i18n/translate';

export default class DateTimePickerControl extends FormControl{
  static propTypes = {
    children: PropTypes.any,
    onChange: PropTypes.func,
    onValueChange: PropTypes.func,
    dataValue: PropTypes.any,
    required: PropTypes.bool,
    disablePicker: PropTypes.bool,
    maxValidDate: PropTypes.string,
    minDate: PropTypes.string,
    placeholder: PropTypes.string,
    format: PropTypes.string,
    valueOverride: PropTypes.bool,
    minTime: PropTypes.any
  };

  static defaultProps = Object.assign({}, FormControl.defaultProps, {
    format: 'yyyy/MM/dd',
    disablePicker: false,
    momentFormat: 'YYYY-MM-DDTHH:mm:ss.sssZ',
    amPmAriaLabel: "Select AM/PM",
    calendarAriaLabel: "Toggle calendar",
    clearAriaLabel: "Clear value",
    dayAriaLabel: "Day",
    monthAriaLabel: "Month",
    hourAriaLabel: "Hour",
    minuteAriaLabel: "Minute",
    nativeInputAriaLabel: "Date",
    secondAriaLabel: "Second",
    yearAriaLabel: "Year"
  });

  constructor(props){
    super(props);
    let value = '', formattedValue = '';
    if (props.dataValue){
      value = props.utc === true ? moment.utc(props.dataValue) : moment(props.dataValue);
      formattedValue = value.format(props.momentFormat);
    }

    this.pattern = GetValidationPattern('datetime');
    if(value){
      value = new Date(value.format(props.momentFormat))
    }
    this.state = { value, formattedValue, manualValueForValidation: null, target: null };
  }

  componentWillReceiveProps(newProps){
    if(newProps.valueOverride){
      let value = newProps.dataValue;
      if (newProps.dataValue){
        value = this.props.utc === true ? moment.utc(newProps.dataValue) : moment(newProps.dataValue);
      }
      let valuesMatch = false;
      if (moment.isMoment(this.state.value)){
        valuesMatch = this.state.value.isSame(value);
      } else if (moment.isMoment(value)){
        valuesMatch = value.isSame(this.state.value);
      } else {
        valuesMatch = this.state.value === value;
      }
      if (!valuesMatch) {
        this.onChange(value, true);
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !(this.props.closeOnSelect && this.state.value === nextState.value);
  }

  checkForManual = () => {
    if (!this.state.target || !this.picker) {
      return;
    }
    const pickerHasValue = this.picker.props.value && this.picker.props.value !== '';
    const currentYear = this.state.manualValueForValidation ? moment(this.state.manualValueForValidation).format('YYYY') : moment().format('YYYY');
    const currentMonth = this.state.manualValueForValidation ? moment(this.state.manualValueForValidation).format('MM') : moment().format('MM');
    const currentDay = this.state.manualValueForValidation ? moment(this.state.manualValueForValidation).format('DD') : moment().format('DD');

    const [_, yearInput, monthInput, dayInput] = ReactDOM.findDOMNode(this.picker).querySelectorAll('input');// eslint-disable-line no-unused-vars
    let year = yearInput.value || currentYear;
    let month = monthInput.value || currentMonth;
    let day = dayInput.value || currentDay;

    year = year.padStart(4, '0');
    month = month.padStart(2, '0');
    day = day.padStart(2, '0');

    const values = this.formatDate(moment(`${year}-${month}-${day}`).format('YYYY-MM-DD'));
    this.setState({ manualValueForValidation: pickerHasValue ? values.value : null }, this.checkError);
  }

  onBlur = (e) => {
    if (typeof this.props.onBlur === 'function'){
      this.props.onBlur(this.state.value);
    }
    this.setState({ target: e.target }, this.checkForManual);
  };

  formatDate = value => {
    const dateFormatValue = value && moment.isMoment(value) ? new Date(value.format(this.props.momentFormat)) : value;
    value = value && moment(value);

    const formattedValue = value && moment.isMoment(value) ? value.format(this.props.momentFormat) : '';

    return { value: dateFormatValue, formattedValue };
  } 

  onChange = (value, skipValueChange) => {
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(value);
    }
    const values = this.formatDate(value);
    if (typeof this.props.onValueChange === 'function' && !skipValueChange) {
      this.props.onValueChange({ [this.props.name]: values.formattedValue });
    }
    this.setState({ value: values.value, formattedValue: values.formattedValue, manualValueForValidation: null });
  }

  isValid = () => {
    let input = ReactDOM.findDOMNode(this.picker).querySelector('input');
    const manualValToCheck = this.state.manualValueForValidation ? this.state.manualValueForValidation : this.state.value;
    if (input) {
      if(!this.props.required && !input.value){
        return true;
      }
      if(this.props.maxValidDate && input.value) {
        return input.validity.valid && moment(input.value).isValid() && moment(input.value).isBefore(this.props.maxValidDate);
      }
      if(this.props.minDate && manualValToCheck) {
        // using state.manualValToCheck for dates before today. input.value will not update before minDate
        return moment(manualValToCheck).isSameOrAfter(moment(this.props.minDate));
      }
      return input.validity.valid && moment(input.value).isValid();
    }
  }

  checkError = () => {
    let message = null;
    const node = ReactDOM.findDOMNode(this.input);
    const manualValToCheck = this.state.manualValueForValidation ? this.state.manualValueForValidation : this.state.value;
    if(node) {
      if(!manualValToCheck && node.validity.valueMissing && this.props.required) {
        message = i18n.t("This field is required");
      } else if (manualValToCheck && !moment(manualValToCheck, 'YYYY-MM-DD').isValid() || (!manualValToCheck && !node.validity.valid)) {
        message = i18n.t("The date you entered is invalid");
      } else if (node.value && this.props.maxValidDate && !moment(node.value).isBefore(this.props.maxValidDate)) {
        message = i18n.t("The date cannot be in the future.");
      } else if (manualValToCheck && this.props.minDate && !moment(manualValToCheck).isSameOrAfter(this.props.minDate)) {
        // using state.value for dates before today. node.value will not update before minDate
        message = i18n.t("The date cannot be in the past.");
      } else if (!node.value && !this.props.required) {
        message = null;
      }
    }
    if (typeof this.props.setError === 'function') {
      this.props.setError(!this.isValid(), message);
    }
  }

  onRef = (picker) => {
    this.picker = picker;
    if (this.picker) {
      const pickerEl = ReactDOM.findDOMNode(this.picker);
      pickerEl.tabIndex = -1; //this is a hack to deal with this bug in react-datetime-picker: https://github.com/wojtekmaj/react-datetime-picker/issues/56
      this.input = pickerEl.querySelector('input');
    }
  }


  render(){
    const { readOnly, setError, name, placeholder, valueOverride, disablePicker, ...props } = this.props;  // eslint-disable-line no-unused-vars
    const inputId = name;
    let format = this.props.timeFormat ? 'yyyy/MM/dd h:mm:ss a' : this.props.format;

    return (<>

      <div>
        <DateTimePicker
          {...props}
          disabled={disablePicker}
          disableClock={!this.props.timeFormat}
          format={format}
          onBlur={this.onBlur}
          onChange={this.onChange}
          ref={this.onRef}
          value={this.state.value}
        />
        <input type="hidden" id={`${inputId}_formatted`} value={this.state.formattedValue} name={name} />
      </div>
    </>);
  }
}
